import type { TFunction } from 'i18next';
import type { Tournament } from '@/api/cms/tournaments';
import { CurrencyCode } from '@/helpers/currency';
import { createCMSQuery, getCMSImageFieldsUrl, getCMSLocale } from '../cms';

export const CMS_TOURNAMENTS_PAGE_SIZE = 20;

export enum TournamentMode {
  ALL = 0,
  ACTIVE = 1,
  COMING_SOON = 2,
  FINISHED = 3,
}

export interface TournamentsObj {
  activeTournaments: Tournament[];
  upcomingTournaments: Tournament[];
  finishedTournaments: Tournament[];
}

export const DAILY_CASH_DROPS_TOURNAMENT_NAME = 'Daily Cash Drops';

export const getTabs = (t: TFunction) => [
  t('tournaments.tabs.all'),
  <span className="pulse-tab" key="active">
    {t('tournaments.tabs.active')}
    <span className="pulse-dot" />
  </span>,
  t('tournaments.tabs.upcoming'),
  t('tournaments.tabs.finished'),
];

const TOURNAMENT_POPULATE = {
  ...getCMSImageFieldsUrl('icon'),
  banner: {
    populate: {
      ...getCMSImageFieldsUrl('background_desktop'),
      ...getCMSImageFieldsUrl('background_mobile'),
      ...getCMSImageFieldsUrl('main_img'),
    },
  },
};

export const getSingleTournament = (name: string, lang = 'en') => {
  const rawQuery = {
    ...getCMSLocale(lang),
    populate: { ...TOURNAMENT_POPULATE },
    filters: {
      name: {
        $containsi: name,
      },
    },
    pagination: {
      pageSize: 1,
    },
  };

  const query = createCMSQuery(rawQuery);

  return `/tournaments-v-2-s?${query}`;
};

export const getActiveTournamentsFilterUrl = (
  lang: string,
  nowISOFixed: string
) => {
  const rawQuery = {
    ...getCMSLocale(lang),
    populate: { ...TOURNAMENT_POPULATE },
    filters: {
      $and: [
        { banner: { duration_from: { $lte: nowISOFixed } } },
        { banner: { duration_to: { $gte: nowISOFixed } } },
      ],
    },
    sort: ['order:asc', 'banner.duration_to:desc'],
  };

  const query = createCMSQuery(rawQuery);

  return `/tournaments-v-2-s?${query}`;
};

export const getFiltersByType = (type: TournamentMode, nowISOFixed: string) => {
  switch (type) {
    case TournamentMode.FINISHED:
      return { banner: { duration_to: { $lte: nowISOFixed } } };
    case TournamentMode.COMING_SOON:
      return { banner: { duration_from: { $gte: nowISOFixed } } };
    default:
      return {};
  }
};

export const getInActiveTournamentsFilterUrl = (
  lang: string,
  type: TournamentMode,
  page = 1,
  nowISOFixed: string
) => {
  if (type === TournamentMode.ACTIVE) {
    return null;
  }
  const rawQuery = {
    ...getCMSLocale(lang),
    pagination: {
      page,
      pageSize: CMS_TOURNAMENTS_PAGE_SIZE,
    },
    populate: { ...TOURNAMENT_POPULATE },
    filters: {
      $not: {
        $and: [
          { banner: { duration_from: { $lte: nowISOFixed } } },
          { banner: { duration_to: { $gte: nowISOFixed } } },
        ],
      },
      ...getFiltersByType(type, nowISOFixed),
    },
    sort: ['banner.duration_to:desc', 'order:asc'],
  };

  const query = createCMSQuery(rawQuery);

  return `/tournaments-v-2-s?${query}`;
};

export const MIN_AMOUNT_DEPOSIT_TO_PLAY: Record<string, number> = {
  [CurrencyCode.USD]: 1,
  [CurrencyCode.CAD]: 1,
  [CurrencyCode.AUD]: 1,
  [CurrencyCode.EUR]: 1,
  [CurrencyCode.NZD]: 1,
  [CurrencyCode.NOK]: 10,
};

export const parseCommaSeparated = (
  values?: string | (string | undefined | null)[]
) => {
  if (!values) return [];

  const arr = Array.isArray(values) ? values : [values];

  return arr.flatMap((val) =>
    (val || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  );
};
